import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, inject, DestroyRef } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-state-group-assign',
    templateUrl: './state-group-assign.component.html',
    styleUrls: ['./state-group-assign.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateGroupAssignComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  assignedEntitiesDataSource = new MatTableDataSource<any>([]);
  assignedDisplayedColumns = [
    'delete',
    'LEID',
    'LE_NAME',
    'ENTITY_TYPE',
    'EIN',
    'consolidated',
    'ADDRESS_LINE1',
    'ADDRESS_LINE2',
    'ADDRESS_CITY',
    'ADDRESS_STATE',
    'ADDRESS_ZIP',
  ];

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Assign Entities to State Group";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  isSaveLoading = false;

  // Table filtering
  globalFilterValue: string = '';

  // Data properties
  rowData: any;
  gridData: any[] = [];
  filterParams: any = {};
  baseUrl: string = '';

  // Group and entity data
  firstSelectedGroup: any = {};
  firstSelectedGroupBackup: any = {};
  stateGroupDisplayName: string = '';
  entityType: string = 'INTERNAL';
  entityTypeBackup: string = 'INTERNAL';

  // Entity lists
  allExternalEntities: any[] = [];
  allInternalEntities: any[] = [];
  allAssignedEntities: any[] = [];
  selectedExternalEntities: any[] = [];
  selectedInternalEntities: any[] = [];
  
  // Backup lists for reset functionality
  allExternalEntitiesBackup: any[] = [];
  allInternalEntitiesBackup: any[] = [];
  allAssignedEntitiesBackup: any[] = [];

  // US States
  allUSStates: any[] = [];

  // Loading states
  isExternalEntityLoading = false;
  isInternalEntityLoading = false;

  // Dropdown states
  externalDropdownOpen = false;
  internalDropdownOpen = false;

  // Global services
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  private initCalled = false;
  private tryInit() {
    // Check if we have either gridData array or rowData object, and filterParams
    const hasGridData = (this.gridData && this.gridData.length > 0) || (this.rowData && Object.keys(this.rowData).length > 0);
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const isDataReady = hasGridData && hasFilterParams;
    
    if (!this.initCalled && isDataReady) {
      this.initCalled = true;
      this.init();
    }
  }

  ngAfterViewInit(): void {
    this.assignedEntitiesDataSource.paginator = this.paginator;
    this.assignedEntitiesDataSource.sort = this.sort;
  }

  ngOnInit(): void {
    this.assignedEntitiesDataSource.filterPredicate = (entity, filter) => {
      const term = filter.trim().toLowerCase();
      if (!term) {
        return true;
      }
      const searchable = [
        entity.LEID,
        entity.HO_CDR_NO,
        entity.HO_REPORTING_PERIOD,
        entity.LE_NAME,
        entity.ENTITY_TYPE,
        entity.EIN,
        entity.ADDRESS_LINE1,
        entity.ADDRESS_LINE2,
        entity.ADDRESS_CITY,
        entity.ADDRESS_STATE,
        entity.ADDRESS_ZIP,
      ];
      return searchable.some(value => String(value ?? '').toLowerCase().includes(term));
    };
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? (rawRow[0]?.data ?? rawRow[0] ?? {})
      : (rawRow || {});
    this.applyGridPayload(gridPayloadFromActiveScreen(activeScreen));
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  private applyGridPayload(grid: any): void {
    if (!grid) {
      this.gridData = [];
      return;
    }
    if (Array.isArray(grid) && grid.length > 0) {
      this.gridData = _.cloneDeep(grid);
    } else if (typeof grid === 'object' && grid.rowData && Object.keys(grid.rowData).length > 0) {
      this.rowData = grid.rowData;
      this.gridData = [{ data: grid.rowData }];
    } else {
      this.gridData = [];
    }
  }

  init() {
    try {
      // Determine first selected group
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.firstSelectedGroup = this.rowData;
      } else if (this.gridData.length > 1) {
        // AngularJS controller already handles this message, so no need to show it here
        this.firstSelectedGroup = this.gridData[0].data;
      } else if (this.gridData.length === 1) {
        this.firstSelectedGroup = this.gridData[0].data;
      } else {
        // AngularJS controller already handles this message, so no need to show it here
        return; // Exit early if no group is selected
      }

      this.firstSelectedGroupBackup = _.cloneDeep(this.firstSelectedGroup);
      this.stateGroupDisplayName = `${this.firstSelectedGroup.GROUP_NAME} - ${this.firstSelectedGroup.MEF_GROUP_KEY}`;

      // Backup initial state for reset
      this.entityTypeBackup = this.entityType;

      // Fetch data
      this.fetchUSStateList();
      this.loadAllEntities('31762'); // External Entities
      this.loadAllEntities('31726'); // Internal Entities
      this.loadAllAssignedEntities();

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in state group assign init:', e);
    }
  }

  fetchUSStateList() {
    this.stateEntityService.fetchUSStateList(this.baseUrl, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.allUSStates = data.jsonObject || [];
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error fetching US state list:', error);
      }
    );
  }

  loadAllEntities(actionId: string) {
    if (actionId === '31762') {
      this.isExternalEntityLoading = true;
    } else {
      this.isInternalEntityLoading = true;
    }

    this.stateEntityService.loadAllEntities(
      this.baseUrl,
      actionId,
      this.firstSelectedGroup.MEF_GROUP_KEY,
      this.globalService,
      this.filterParams,
      this.firstSelectedGroup
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          const entities = (data.jsonObject || []).map((entity: any) => {
            entity.VALUE = entity.LE_KEY;
            entity.LABEL = `${entity.LEID} - ${entity.LE_NAME} - ${entity.CDR_NO} - ${entity.REPORTING_PERIOD}`;
            return entity;
          });

          if (actionId === '31762') {
            this.allExternalEntities = entities;
            this.allExternalEntitiesBackup = _.cloneDeep(entities);
            this.isExternalEntityLoading = false;
          } else {
            this.allInternalEntities = entities;
            this.allInternalEntitiesBackup = _.cloneDeep(entities);
            this.isInternalEntityLoading = false;
          }

          this.markAllSavedEntities();
        } else {
          if (data.errorMessage) {
            this._snackBar.open(data.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading entities:', error);
        if (actionId === '31762') {
          this.isExternalEntityLoading = false;
        } else {
          this.isInternalEntityLoading = false;
        }
        this.cdr.detectChanges();
      }
    );
  }

  loadAllAssignedEntities() {
    this.stateEntityService.loadAllAssignedEntities(
      this.baseUrl,
      this.firstSelectedGroup.MEF_GROUP_KEY,
      this.globalService,
      this.filterParams,
      this.firstSelectedGroup
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.allAssignedEntities = data.jsonObject || [];
          this.allAssignedEntitiesBackup = _.cloneDeep(this.allAssignedEntities);
          this.refreshAssignedEntitiesTable();
          this.markAllSavedEntities();
        } else {
          if (data.errorMessage) {
            this._snackBar.open(data.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('An error has occurred. If you continue to experience this issue please visit the help page to contact support.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading assigned entities:', error);
        this.cdr.detectChanges();
      }
    );
  }

  markAllSavedEntities() {
    // Restore from backup to ensure we start fresh
    if (this.allExternalEntitiesBackup.length > 0) {
      this.allExternalEntities = _.cloneDeep(this.allExternalEntitiesBackup);
    }
    if (this.allInternalEntitiesBackup.length > 0) {
      this.allInternalEntities = _.cloneDeep(this.allInternalEntitiesBackup);
    }

    // Clear selections
    this.selectedExternalEntities = [];
    this.selectedInternalEntities = [];

    // Remove already assigned entities from available lists
    for (let i = 0; i < this.allAssignedEntities.length; i++) {
      const assignedLE_KEY = this.allAssignedEntities[i].LE_KEY;

      // Remove from external entities
      const externalIndex = _.findIndex(this.allExternalEntities, { LE_KEY: assignedLE_KEY });
      if (externalIndex !== -1) {
        this.allExternalEntities.splice(externalIndex, 1);
      }

      // Remove from internal entities
      const internalIndex = _.findIndex(this.allInternalEntities, { LE_KEY: assignedLE_KEY });
      if (internalIndex !== -1) {
        this.allInternalEntities.splice(internalIndex, 1);
      }
    }
  }

  onAssignEntity() {
    const selectedEntities = this.entityType === 'INTERNAL' ? this.selectedInternalEntities : this.selectedExternalEntities;

    if (selectedEntities.length === 0) {
      this._snackBar.open('No Entity selected', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    // Close dropdowns when assigning
    this.externalDropdownOpen = false;
    this.internalDropdownOpen = false;

    this.crudLoading = true;

    const entityData: any[] = [];
    selectedEntities.forEach((entity: any) => {
      const row = {
        "CREATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
        "UPDATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
        "ENTITY_TYPE": "REG",
        "EXTERNAL_Y_N": this.entityType === 'EXTERNAL' ? 'Y' : 'N',
        "FGN_DOM_FLAG": "",
        "FILER_STATUS": "A",
        "FORM_FILER_IND": "",
        "LE_KEY": entity.LE_KEY,
        "LE_NAME": entity.LE_NAME,
        "MEF_GROUP_KEY": this.firstSelectedGroup.MEF_GROUP_KEY,
        "TAX_YEAR": entity.TAX_YEAR,
        "TGT_MEF_CONSOL_ENTITIES": entity.LEID,
        "REPORTING_PERIOD": entity.REPORTING_PERIOD,
        "CDR_NO": entity.CDR_NO,
        "CREATED_ON": "",
        "UPDATED_ON": ""
      };
      entityData.push(row);
    });

    this.stateEntityService.assignEntityToGroup(
      this.baseUrl,
      entityData,
      this.globalService,
      this.filterParams,
      this.firstSelectedGroup,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Entity Assigned successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Clear selections
          this.selectedExternalEntities = [];
          this.selectedInternalEntities = [];
          // Reload assigned entities
          this.loadAllAssignedEntities();
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in assigning the groups. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        console.error('Error assigning entity:', error);
        this._snackBar.open('Error occurred while assigning entity. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  onUnassignEntity(entity: any) {
    // Show confirmation dialog before unassigning
    const confirmed = window.confirm('Do you want to continue unassigning entity?');
    
    if (!confirmed) {
      return;
    }

    this.crudLoading = true;

    this.stateEntityService.unassignEntityFromGroup(
      this.baseUrl,
      entity,
      this.globalService,
      this.filterParams,
      this.firstSelectedGroup,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Entity Unassigned successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Reload assigned entities
          this.loadAllAssignedEntities();
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in unassigning the entity. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        console.error('Error unassigning entity:', error);
        this._snackBar.open('Error occurred while unassigning entity. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  isValidAddress(): boolean {
    for (let i = 0; i < this.allAssignedEntities.length; i++) {
      const entity = this.allAssignedEntities[i];
      if (entity.EXTERNAL_Y_N === 'Y') {
        const hasAddressLine1 = !!entity.ADDRESS_LINE1;
        const hasCity = !!entity.ADDRESS_CITY;
        const hasState = !!entity.ADDRESS_STATE;
        const hasZip = !!entity.ADDRESS_ZIP;

        // All address fields should be either all filled or all empty
        if (hasAddressLine1 !== hasCity || hasAddressLine1 !== hasState || hasAddressLine1 !== hasZip) {
          return false;
        }
      }
    }
    return true;
  }

  onSaveAddress() {
    if (!this.isValidAddress()) {
      this._snackBar.open('One or more fields of Address is empty.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveLoading = true;

    this.stateEntityService.saveAssignedEntityAddress(
      this.baseUrl,
      this.allAssignedEntities,
      this.globalService,
      this.filterParams,
      this.firstSelectedGroup
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Address saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Reload assigned entities
          this.loadAllAssignedEntities();
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in assigning the groups. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveLoading = false;
        console.error('Error saving address:', error);
        this._snackBar.open('Error occurred while saving address. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  getStateName(stateCode: string): string {
    const state = this.allUSStates.find(s => s.STATE_CODE === stateCode);
    return state ? state.STATE_DESC : '';
  }

  // Multi-select helper methods
  isEntitySelected(entity: any, type: 'INTERNAL' | 'EXTERNAL'): boolean {
    const list = type === 'INTERNAL' ? this.selectedInternalEntities : this.selectedExternalEntities;
    return list.some(e => e.LE_KEY === entity.LE_KEY);
  }

  toggleEntitySelection(entity: any, type: 'INTERNAL' | 'EXTERNAL'): void {
    const list = type === 'INTERNAL' ? this.selectedInternalEntities : this.selectedExternalEntities;
    const index = list.findIndex(e => e.LE_KEY === entity.LE_KEY);
    
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(entity);
    }
    this.cdr.detectChanges();
  }

  selectAllExternalEntities(): void {
    this.selectedExternalEntities = [...this.allExternalEntities];
    this.cdr.detectChanges();
  }

  clearAllExternalEntities(): void {
    this.selectedExternalEntities = [];
    this.cdr.detectChanges();
  }

  selectAllInternalEntities(): void {
    this.selectedInternalEntities = [...this.allInternalEntities];
    this.cdr.detectChanges();
  }

  clearAllInternalEntities(): void {
    this.selectedInternalEntities = [];
    this.cdr.detectChanges();
  }

  onEntityTypeChange(): void {
    // Close both dropdowns when entity type changes
    this.externalDropdownOpen = false;
    this.internalDropdownOpen = false;
    this.cdr.detectChanges();
  }

  toggleExternalDropdown(event: Event): void {
    event.stopPropagation();
    this.externalDropdownOpen = !this.externalDropdownOpen;
    this.internalDropdownOpen = false; // Close other dropdown
    
    if (this.externalDropdownOpen) {
      this.addClickListener();
    }
    this.cdr.detectChanges();
  }

  toggleInternalDropdown(event: Event): void {
    event.stopPropagation();
    this.internalDropdownOpen = !this.internalDropdownOpen;
    this.externalDropdownOpen = false; // Close other dropdown
    
    if (this.internalDropdownOpen) {
      this.addClickListener();
    }
    this.cdr.detectChanges();
  }

  private clickListenerAdded = false;
  private addClickListener(): void {
    if (this.clickListenerAdded) return;
    
    this.clickListenerAdded = true;
    const listener = () => {
      this.externalDropdownOpen = false;
      this.internalDropdownOpen = false;
      this.clickListenerAdded = false;
      this.cdr.detectChanges();
      document.removeEventListener('click', listener);
    };
    
    // Use setTimeout to prevent immediate closing
    setTimeout(() => {
      document.addEventListener('click', listener);
    }, 0);
  }

  reset() {
    // Restore all backed up data
    this.firstSelectedGroup = _.cloneDeep(this.firstSelectedGroupBackup);
    this.allAssignedEntities = _.cloneDeep(this.allAssignedEntitiesBackup);
    
    // Reset entity type to initial value
    this.entityType = this.entityTypeBackup;
    
    // Clear selections
    this.selectedExternalEntities = [];
    this.selectedInternalEntities = [];
    
    // Close dropdowns
    this.externalDropdownOpen = false;
    this.internalDropdownOpen = false;
    
    // Clear table search/filter
    this.globalFilterValue = '';
    
    this.refreshAssignedEntitiesTable();
    if (this.paginator) {
      this.paginator.pageSize = 10;
      this.paginator.firstPage();
    }
    if (this.sort) {
      this.sort.sort({ id: '', start: 'asc', disableClear: false });
      this.sort.active = '';
      this.sort.direction = '';
    }
    
    // Refilter the available entities based on assigned ones
    this.markAllSavedEntities();
    
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  onGlobalFilter(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.globalFilterValue = inputElement.value;
    this.assignedEntitiesDataSource.filter = inputElement.value.trim().toLowerCase();
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  private refreshAssignedEntitiesTable(): void {
    this.assignedEntitiesDataSource.data = [...this.allAssignedEntities];
    this.assignedEntitiesDataSource.filter = (this.globalFilterValue || '').trim().toLowerCase();
  }
}
